import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail, MapPin, Phone } from 'lucide-react';
import axios from 'axios';
import { brand, nav } from '../mock';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Please enter a valid email', description: 'We use it only to send occasional insights.' });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast({ title: 'You\u2019re on the list', description: 'Thanks for subscribing. A gentle note will land in your inbox now and then.' });
      setEmail('');
    } catch (err) {
      toast({ title: 'Couldn\u2019t sign you up', description: 'Please try again in a moment.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="relative overflow-hidden bg-[#152B20] text-[#EEE7D3]">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 20% 20%, #B8CE7F 0%, transparent 40%), radial-gradient(circle at 85% 70%, #E27B47 0%, transparent 35%)'
      }} />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-10">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#B8CE7F]">Newsletter</div>
            <h3 className="font-serif-display text-4xl md:text-5xl mt-4 leading-[1.05]">
              Occasional insights, never noise.
            </h3>
            <p className="mt-5 text-[#D5CDB8] max-w-md">
              Sign up for reflections on leadership, coaching and behavioural insights. Free resources land now and then.
            </p>
            <form onSubmit={onSubscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-transparent border border-[#3B5443] focus:border-[#B8CE7F] rounded-full px-5 py-3 text-sm placeholder:text-[#8A9A8B] outline-none btn-transition"
              />
              <button
                type="submit"
                disabled={busy}
                className="btn-transition inline-flex items-center justify-center gap-2 bg-[#E27B47] hover:bg-[#D06A38] disabled:opacity-70 text-[#FAF6EE] rounded-full px-6 py-3 text-sm"
              >
                {busy ? 'Signing up...' : 'Sign up'} <ArrowUpRight className="w-4 h-4" />
              </button>
            </form>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#B8CE7F]">Explore</div>
            <ul className="mt-5 space-y-3">
              {nav.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="link-underline text-[15px] text-[#EEE7D3]">{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#B8CE7F]">Contact</div>
            <ul className="mt-5 space-y-4 text-[15px]">
              <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-1 text-[#B8CE7F]" /><a href={`mailto:${brand.email}`} className="link-underline">{brand.email}</a></li>
              <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-1 text-[#B8CE7F]" /><span>{brand.phone}</span></li>
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-1 text-[#B8CE7F]" /><span>{brand.location}</span></li>
            </ul>
            <div className="mt-6 flex gap-3">
              {brand.social.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer"
                  className="btn-transition text-[12px] tracking-wide uppercase border border-[#3B5443] hover:border-[#B8CE7F] rounded-full px-4 py-2">
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#2A4132] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-serif-display text-2xl">{brand.full}</div>
          <div className="text-[12px] text-[#8FA090]">© {new Date().getFullYear()} {brand.full}. All rights reserved. · Privacy · Terms</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
